import { useLazyQuery, useQuery } from '@apollo/client';
import { useCallback, useMemo, useState } from 'react';

import {
  DROPOFFS_MAX_RESULTS,
  MAX_NUMBER_OF_TIMELINES,
} from '../config/constants';
import {
  GET_GREASE_TRAPS,
  GET_SINGLE_GREASE_TRAP,
  GET_VENDOR_GREASE_TRAPS,
} from '../services/graphql/vendors/grease-traps/greaseTrapQuery';
import type {
  GetSingleGreaseTrapResponse,
  GreaseTrap,
  GreaseTrapListQuery,
} from '../types/greaseTrap';

type UseGreaseTrapListProps = {
  vendorId?: string;
  statusFilter?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  limit?: number;
};

const getListData = (data?: GreaseTrapListQuery, isVendorView?: boolean) => {
  if (!data) {
    return [];
  }

  return isVendorView
    ? data.getVendorGreaseTraps || []
    : data.getGreaseTraps || [];
};

export const useGreaseTrapList = ({
  vendorId,
  statusFilter,
  startDate,
  endDate,
  limit = DROPOFFS_MAX_RESULTS,
}: UseGreaseTrapListProps) => {
  const [greaseTraps, setGreaseTraps] = useState<GreaseTrap[]>([]);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const isVendorView = useMemo(() => !!vendorId, [vendorId]);

  const query = isVendorView ? GET_VENDOR_GREASE_TRAPS : GET_GREASE_TRAPS;

  const { fetchMore, loading, error, refetch } = useQuery<GreaseTrapListQuery>(
    query,
    {
      variables: {
        vendorId,
        statusFilter: statusFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        offset: 0,
        limit,
      },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      onCompleted: (res) => {
        const data = getListData(res, isVendorView);
        setGreaseTraps(data);
        setHasNextPage(data.length === limit);
      },
    },
  );

  const [fetchSingleQuery, { loading: loadingSingleQuery }] =
    useLazyQuery<GetSingleGreaseTrapResponse>(GET_SINGLE_GREASE_TRAP, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    });

  const refetchGreaseTraps = () => {
    setHasNextPage(true);
    setGreaseTraps([]);

    refetch({
      vendorId,
      statusFilter: statusFilter || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      offset: 0,
      limit,
    });
  };

  const fetchMoreGreaseTraps = useCallback(() => {
    if (greaseTraps.length === MAX_NUMBER_OF_TIMELINES) {
      return;
    }

    fetchMore({
      variables: {
        vendorId,
        statusFilter: statusFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        offset: greaseTraps.length,
        limit,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        const newData = getListData(fetchMoreResult, isVendorView);
        const merged = [...greaseTraps, ...newData].reduce(
          (acc: GreaseTrap[], current: GreaseTrap) => {
            if (!acc.some((item) => item.id === current.id)) {
              acc.push(current);
            }

            return acc;
          },
          [],
        );

        setGreaseTraps(merged);
        setHasNextPage(newData.length === limit);

        if (isVendorView) {
          return {
            ...prev,
            getVendorGreaseTraps: merged,
          };
        }

        return {
          ...prev,
          getGreaseTraps: merged,
        };
      },
    });
  }, [
    fetchMore,
    greaseTraps,
    isVendorView,
    limit,
    statusFilter,
    vendorId,
    startDate,
    endDate,
  ]);

  const refetchSingleGreaseTrap = (id: string) => {
    fetchSingleQuery({
      variables: { id },
      onCompleted: (res) => {
        const currentGreaseTrap = res.getSingleGreaseTrap;
        const index = greaseTraps.findIndex((item) => item.id === id);

        if (index > -1) {
          const next = [...greaseTraps];
          next[index] = currentGreaseTrap;
          setGreaseTraps(next);
        }
      },
    });
  };

  return {
    fetchMoreGreaseTraps,
    greaseTraps,
    hasNextPage,
    loading,
    errorTimeline: error,
    loadingSingleQuery,
    refetchGreaseTraps,
    refetchSingleGreaseTrap,
  };
};

export default useGreaseTrapList;
